// The guided tour of the survey form, for whoever fills a survey in: usually
// the engineer on site, on a phone. It opens once, the first time somebody
// opens a survey they can still change, other than one submitted and waiting
// for review: a PM opening that one is reviewing it, not filling it in.
//
// Steps point at [data-tour] anchors on SurveyForm, DeskSurveySections and
// surveyFormParts in the order the form reads. Room surveys, desk
// standardisations and desk moves (DeskMoveSections) have different middles,
// so those steps are gated on `kind`; the photos step
// on `photos` (false for a paper-only survey, which takes none), and the
// review step on `canReview`.

export const SURVEY_FORM_TOUR_ID = 'survey-form';

// `kind` is room, desks or moves; an older caller that sends only isDesks means room or desks.
const kindIn = (c) => c.kind || (c.isDesks ? 'desks' : 'room');
const room = (c) => kindIn(c) === 'room';
const desks = (c) => kindIn(c) === 'desks';
const moves = (c) => kindIn(c) === 'moves';

export const surveyFormTourSteps = [
  {
    title: 'Filling in a survey',
    target: '[data-tour="survey-header"]',
    body: [
      'This walks you through the form once. It works on a phone, so you can fill it in on site as you go.',
      'Use Next (or the arrow keys) to walk through, or Skip. The ? button replays it.',
    ],
  },
  {
    title: 'The form comes from Ops',
    target: '[data-tour="survey-word"]',
    body: [
      'Download the form here each time you need it: the Word form for a room survey, the Excel form for a desk standardisation or a desk move. It is stamped with this survey\'s reference and the form version, so do not reuse an old copy or one from another job.',
      'Fill it in on site, or print it where phones and cameras are not allowed.',
    ],
  },
  {
    title: 'Upload it back when it is done',
    target: '[data-tour="survey-upload"]',
    body: [
      'Upload completed form reads the form back into this survey and keeps the file with it. Anything the form did not answer clearly is listed for you to check. A form for another survey is refused.',
      'You can also type the answers straight into this page instead.',
    ],
  },
  {
    title: 'Job details',
    target: '[data-tour="survey-job"]',
    body: [
      'The PM fills in most of this. Check the room or area and the location are right, and correct them if not.',
    ],
  },
  {
    title: 'Can it be done as asked?',
    target: '[data-tour="survey-can-do"]',
    when: room,
    body: [
      'Choose one. If it can be done but differently, or not at all, say what you would do instead: the PM needs it to price the job.',
      'Then list the tasks in order (question 3), the network ports and whether each is live (question 4), and the kit already in the room (question 5).',
    ],
  },
  {
    title: 'Kit and materials needed',
    target: '[data-tour="survey-kit"]',
    when: room,
    body: [
      'This is the part that gets priced. Put the quantity in Qty and the size or spec beside it, one item per row.',
    ],
  },
  {
    title: 'Standards',
    target: '[data-tour="survey-desk-standards"]',
    when: desks,
    body: [
      'Each standard is a kind of desk: the networks it needs, the minimum sockets and the kit per desk. Check these match the client\'s requirement before you start on the desks.',
    ],
  },
  {
    title: 'The desks',
    target: '[data-tour="survey-desks"]',
    when: desks,
    body: [
      'Add the desks by number, such as 66-81, then open each card. Say whether you checked it, choose its standard, record the sockets available and the port labels exactly as written on the floor box or faceplate.',
      'Only enter kit where the desk differs from its standard, and record kit already at the desk that can be reused. Same as the desk above copies the shared answers to the next desk.',
      'Use the Not answered button to find the desks you have not done yet.',
    ],
  },
  {
    title: 'Totals',
    target: '[data-tour="survey-desk-totals"]',
    when: desks,
    body: [
      'Ops works out the kit to order, the desks short of sockets and the ports not recorded as you go. The desk cards are tagged with the same problems, so you can fix them while you are still on site.',
      'Below this, record any workplace adjustments, extra equipment the client must approve, and what you found against each risk.',
    ],
  },
  {
    title: 'The moves',
    target: '[data-tour="survey-moves"]',
    when: moves,
    body: [
      'One card per person, desk, team or piece of equipment that moves. Add them with Add moves, then open each card: who moves, the desk and area they leave and the ones they go to, the wave, priority and move type.',
      'Same as the move above copies what a team usually shares, such as the areas, date, wave and kit. Use Not ready to find the moves with something in the way.',
    ],
  },
  {
    title: 'Ready at the new desk, and the ports',
    target: '[data-tour="survey-moves"]',
    when: moves,
    body: [
      'Say whether the destination, the network and the power are ready: Yes, No, or Not checked if you could not check.',
      'Choose the networks the user needs, then record each port exactly as labelled at the old desk and the new one, and whether it is live. A needed network with no live port at the new desk shows on the card and in the summary.',
      'Record the kit that travels: monitors, monitor arms (with how many of each), the dock, keyboard and mouse, headset, cables, adaptors and any accessibility kit.',
    ],
  },
  {
    title: 'Summary',
    target: '[data-tour="survey-move-summary"]',
    when: moves,
    body: [
      'Worked out from the moves, as the workbook\'s Move Summary tab was: moves ready, blocked and complete, what is not ready, monitors and monitor arms, and each move with something to resolve.',
      'Below it, record equipment and issues that must follow a user, and the risks, each with an owner and a date.',
    ],
  },
  {
    title: 'Move day',
    when: moves,
    body: [
      'Each card ends with move day: the status, what is blocking it, move complete, the user check, and who packed the kit and where it is staged.',
      'Once the PM marks the survey reviewed, the rest of the survey is locked but move day stays open. Save move day records the moves as they happen, and the move sign-off records the outcome.',
    ],
  },
  {
    title: 'Engineers and time',
    target: '[data-tour="survey-time"]',
    body: [
      'How many engineers, for how many days or shifts each, in or out of hours, and whether it is one visit or phased. The labour is priced from this, so the survey cannot be submitted without the engineers and the days.',
    ],
  },
  {
    title: 'Site and access',
    target: '[data-tour="survey-site"]',
    body: [
      'Leave an answer blank when the standard assumption holds: that is how the job is priced. Only answer what is different, such as a ladder or tower, no power, an area that is not clear, or a permit or escort.',
      'Anything that could stop the job, delay it or cost extra, including anything someone else must do first, goes in question 9 on a room survey and in the risks on a desk survey.',
    ],
  },
  {
    title: 'Photos',
    target: '[data-tour="survey-photos"]',
    when: (c) => !!c.photos,
    body: [
      'Take photos of the floor boxes, faceplates, ceiling and anything awkward. On a phone, Add photos opens the camera.',
    ],
  },
  {
    title: 'What changes the price',
    target: '[data-tour="survey-standard"]',
    body: [
      'Not standard lists every answer that differs from the standard assumptions. Check it reads right before you submit: it is what the PM prices.',
    ],
  },
  {
    title: 'Save and submit',
    target: '[data-tour="survey-actions"]',
    body: [
      'Save as you go. Submit for review stays greyed out until the survey is complete, and the line under it says what is still missing.',
      'Once submitted, the PM reviews it. If they send it back, it returns to you to finish; once they mark it reviewed it is locked.',
    ],
  },
  {
    title: 'Reviewing',
    target: '[data-tour="survey-actions"]',
    when: (c) => !!c.canReview,
    body: [
      'You can review surveys. When one is submitted, Mark reviewed and Send back appear here, and Reopen on a reviewed one.',
    ],
  },
];
